import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { SchemaFieldIcon } from '@rossum/components/schema-field-icon/SchemaFieldIcon';
import {
  IconAlertTriangle,
  IconFile,
  IconHelpHexagon,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import { Stack, SvgIcon, Tooltip, Typography } from '@rossum/ui/material';
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
} from '@rossum/ui/x-data-grid-pro';
import { convertRateToPercentage, EMPTY_PLACEHOLDER } from '../../core/utils';
import { RenderBlockedDocumentCounts } from '../components';
import { RenderHeader } from '../components/column-renderers/RenderHeader';
import { AutomationInsightssRowModel } from '../types';

type UseAutomationInsightsColumnsProps = { hasRoughPrecision: boolean };

export const useAutomationInsightsColumns = ({
  hasRoughPrecision,
}: UseAutomationInsightsColumnsProps) => {
  const intl = useIntl();

  return useMemo<GridColDef<AutomationInsightssRowModel>[]>(() => {
    return [
      {
        field: 'type',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.table.headers.type',
        }),
        renderHeader: params => (
          <RenderHeader label={params.colDef.headerName} />
        ),
        renderCell: params => {
          const { type, isDeleted } = params.row;
          return (
            <Stack sx={{ height: '100%', justifyContent: 'center' }}>
              {isDeleted ? (
                <SvgIcon fontSize="large" sx={{ color: 'text.disabled' }}>
                  <IconTrash />
                </SvgIcon>
              ) : type ? (
                type === 'annotation-level-blocker' ? (
                  <SvgIcon fontSize="large">
                    <IconFile />
                  </SvgIcon>
                ) : (
                  <SchemaFieldIcon type={type} />
                )
              ) : (
                <SvgIcon fontSize="large">
                  <IconHelpHexagon />
                </SvgIcon>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'label',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.table.headers.label',
        }),
        renderHeader: params => (
          <RenderHeader label={params.colDef.headerName} />
        ),
        flex: 1,
        renderCell: params => {
          if (params.row.isDeleted) {
            return (
              <Stack
                sx={{
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Tooltip
                  title={intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.deletedField.tooltip',
                  })}
                  placement="top"
                >
                  <Typography
                    variant="h6"
                    color="text.disabled"
                    sx={{ display: 'inline-block' }}
                  >
                    {params.row.schemaId}
                  </Typography>
                </Tooltip>
              </Stack>
            );
          }

          const label =
            params.row.type === 'annotation-level-blocker'
              ? intl.formatMessage({
                  id: 'containers.settings.queues.queue.automation.table.annotationLevelBlockerLabel',
                })
              : params.row.schemaId;
          return (
            <Stack sx={{ height: '100%', justifyContent: 'center' }}>
              <Typography variant="h6">{params.row.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'blockers',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.table.headers.blockedDocuments',
        }),
        renderHeader: params => (
          <RenderHeader label={params.colDef.headerName} />
        ),
        flex: 1,
        renderCell: params => {
          return (
            <RenderBlockedDocumentCounts
              blockers={params.row.blockers}
              queueId={params.row.queueId}
            />
          );
        },
        valueGetter: (_, row) => R.sum(row.blockers.map(b => b.documentCount)),
      },
      {
        field: 'precision',
        headerName: intl.formatMessage({
          id: 'containers.settings.queues.queue.automation.table.headers.precision',
        }),
        renderHeader: params => (
          <RenderHeader
            label={params.colDef.headerName}
            icon={
              hasRoughPrecision ? (
                <Tooltip
                  title={intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.table.headers.precision.tooltip',
                  })}
                >
                  <SvgIcon color="warning">
                    <IconAlertTriangle />
                  </SvgIcon>
                </Tooltip>
              ) : null
            }
          />
        ),
        flex: 1,
        // keep the cell value numeric so sorting compares numbers instead of
        // the formatted string, where e.g. '100.0%' would sort before '99.5%'
        type: 'number' as const,
        align: 'right' as const,
        headerAlign: 'left' as const,
        renderCell: params => (
          <Stack sx={{ height: 1, justifyContent: 'center' }}>
            <Typography
              variant="body2"
              color={hasRoughPrecision ? 'textSecondary' : 'textPrimary'}
            >
              {params.formattedValue}
            </Typography>
          </Stack>
        ),
        valueFormatter: value =>
          R.isNumber(value)
            ? convertRateToPercentage(value, { padding: 1 })
            : EMPTY_PLACEHOLDER,
      },
      { ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF, type: 'actions' as const },
    ];
  }, [intl, hasRoughPrecision]);
};
